import React, { useCallback, useContext } from 'react';
import { Resizable } from 'react-resizable';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import { TableContext } from './context';
import HeaderTitle from './headerTitle';
import VirtualizedGrid from '../virtualizedGrid';

const Header = React.forwardRef((props, ref) => {
    const {
        columns,
        height,
        width,
        virtualized,
        className,
        style,
        headerClassName,
        headerStyle,
        scrollY,
        onScroll,
        onResize
    } = props;
    const {
        ScrollbarWidth,
        resizable,
        prerender,
        columnBordered
    } = useContext(TableContext);

    const columnWidth = useCallback((col, index) => (
        index === columns.length - 1 && scrollY ? col.width - ScrollbarWidth : col.width
    ), [columns, scrollY, ScrollbarWidth]);
    const rowHeight = useCallback(() => height, [height]);

    return columns?.length ? (
        <div className="relative" style={{ height, width }}>
            <VirtualizedGrid
                ref={ref}
                virtualized={virtualized}
                prerender={prerender}
                width={scrollY ? width - ScrollbarWidth : width}
                height={height}
                columns={columns}
                rows={['']}
                columnWidth={columnWidth}
                rowHeight={rowHeight}
                className={clsx(
                    'overflow-y-hidden hidden-scrollbar border-b border-gray bg-table-header',
                    className
                )}
                style={style}
                onScroll={onScroll}
            >
                {({ col, colIndex }) => (
                    <Resizable
                        key={col.dataIndex || uuidv4()}
                        width={col.width}
                        height={0}
                        handle={resizable && !!onResize && (
                            <span
                                className="absolute -right-1 bottom-0
                                    w-2 h-full cursor-col-resize z-10"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                        onResize={(_, { size }) => {
                            onResize?.({ dataIndex: col.dataIndex, width: size.width });
                        }}
                    >
                        <div
                            className={clsx(
                                'relative h-full p-0.5 overflow-hidden',
                                { 'border-r': columnBordered && colIndex !== columns.length - 1 },
                                headerClassName?.(col, colIndex)
                            )}
                            style={{
                                width: colIndex === columns.length - 1 && scrollY ?
                                    col.width - ScrollbarWidth : col.width,
                                ...headerStyle?.(col, colIndex)
                            }}
                        >
                            <HeaderTitle col={col} />
                        </div>
                    </Resizable>
                )}
            </VirtualizedGrid>
            {scrollY && (
                <div className="h-full absolute top-0 right-0">
                    <div
                        className="absolute right-0 border-b border-l border-gray
                            pointer-events-none bg-table-header"
                        style={{ height, width: ScrollbarWidth + 1 }}
                    />
                </div>
            )}
        </div>
    ) : (
        <div
            className="border-b border-gray bg-table-header"
            style={{
                width: scrollY ? width - ScrollbarWidth : width,
                height
            }}
        />
    );
});

export default Header;
